import { LeadPriorityPresentation } from '@/components/Filters/LeadPriorityDropdownFilters';
import { LeadStateTagPresentation } from '@/components/Filters/LeadStateFilters';
import PhoneNumberFormat from '@/components/format/PhoneNumberFormat';
import {
  LeadActivityEvents,
  LeadActivityModel,
  LeadActivityType,
} from '@/typings/models/LeadActivity';
import { F } from '@/utils/intl';
import { isCallbackFromLocation } from '@/utils/serviceRequest';
import { ArrowRightOutlined, FileOutlined } from '@ant-design/icons';
import { Typography } from 'antd';
import classNames from 'classnames';
import dayjs from 'dayjs';
import utcPlugin from 'dayjs/plugin/utc';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import React from 'react';
import { useDispatch, useIntl } from 'umi';
import {
  getActivityTitle,
  getExtraFieldValueText,
  humanizeFieldKey,
} from './activityPresentation';
import styles from './index.less';
import { getCallDuration } from './utils';

dayjs.extend(utcPlugin);

// Activity types whose payload points at an appointment that can be opened
// (new_value.id, or new_value.service_request.id for serviceRequestUpdate
// payloads). These also render the appointment details block inside the card.
const APPOINTMENT_LINK_TYPES = new Set<LeadActivityType>([
  LeadActivityEvents.appointment_requested,
  LeadActivityEvents.appointment_booked,
  LeadActivityEvents.appointment_cancelled,
  LeadActivityEvents.appointment_cancelled_by_customer,
  LeadActivityEvents.appointment_declined,
  LeadActivityEvents.appointment_rescheduled,
  LeadActivityEvents.appointment_rescheduled_by_customer,
  LeadActivityEvents.appointment_start_time_changed,
  LeadActivityEvents.appointment_end_time_changed,
  LeadActivityEvents.appointment_assignee_changed,
  LeadActivityEvents.appointment_branch_service_changed,
  LeadActivityEvents.appointment_attendance_status_changed,
  LeadActivityEvents.call_back,
  LeadActivityEvents.callback_from_location,
]);

// Activity types carrying a serviceRequestUpdate payload — a snapshot of one
// appointment field: {property, value, service_request}. The changed value
// lives under `.value`; the owning appointment under `.service_request`.
const SERVICE_REQUEST_UPDATE_TYPES = new Set<LeadActivityType>([
  LeadActivityEvents.appointment_assignee_changed,
  LeadActivityEvents.appointment_branch_service_changed,
  LeadActivityEvents.appointment_attendance_status_changed,
  LeadActivityEvents.appointment_start_time_changed,
  LeadActivityEvents.appointment_end_time_changed,
]);

// Activity types whose new_value.id points at a calendar event (blocker) that can be opened.
const CALENDAR_EVENT_LINK_TYPES = new Set<LeadActivityType>([
  LeadActivityEvents.calendar_event_created,
  LeadActivityEvents.calendar_event_updated,
]);

// File activities render the uploaded file's name as a detail row. The
// customer/system distinction already lives in the headline (dedicated event
// types) and the footer author, so no extra source tag is needed.
const FILE_TYPES = new Set<LeadActivityType>([
  LeadActivityEvents.file_uploaded,
  LeadActivityEvents.file_deleted,
  LeadActivityEvents.customer_file_uploaded,
  LeadActivityEvents.customer_file_deleted,
]);

// Reschedule-like types render the old time struck out next to the new one
// (same presentation as the reschedule emails) instead of two separate lists.
const RESCHEDULE_TYPES = new Set<LeadActivityType>([
  LeadActivityEvents.appointment_rescheduled,
  LeadActivityEvents.appointment_rescheduled_by_customer,
  LeadActivityEvents.appointment_start_time_changed,
  LeadActivityEvents.appointment_end_time_changed,
]);

type ActivityClickTarget =
  | { appointmentId: string }
  | { calendarEventId: string };

const getActivityClickTarget = (
  activity: LeadActivityModel,
): ActivityClickTarget | null => {
  const id = activity.new_value?.service_request?.id ?? activity.new_value?.id;
  if (!id) return null;
  if (APPOINTMENT_LINK_TYPES.has(activity.activity_type))
    return { appointmentId: id };
  if (CALENDAR_EVENT_LINK_TYPES.has(activity.activity_type))
    return { calendarEventId: id };
  return null;
};

/**
 * Safely converts an arbitrary activity value into a renderable string.
 * Prevents "Objects are not valid as a React child" crashes that would
 * otherwise break the whole activity timeline when a value is a raw object.
 */
const toDisplayString = (val: any): string => {
  if (val === null || val === undefined) return '';
  if (typeof val === 'object') {
    const picked = get(
      val,
      'label',
      get(val, 'name', get(val, 'title', undefined)),
    );
    if (picked !== undefined && picked !== null && typeof picked !== 'object') {
      return String(picked);
    }
    const primitives = Object.values(val).filter(
      (v) => v !== null && v !== undefined && typeof v !== 'object',
    );
    return primitives.length ? primitives.map(String).join(', ') : '';
  }
  return String(val);
};

// User summaries from the activities API carry `fullname` (not always
// firstname/lastname) — prefer the parts, then fullname, then email.
const getEditorName = (editor: LeadActivityModel['editor']): string =>
  [editor?.firstname, editor?.lastname].filter(Boolean).join(' ') ||
  editor?.fullname ||
  editor?.email ||
  '';

/** Slightly emphasised in-sentence value (plain text — no tags/avatars). */
const Value = ({ children }: { children: React.ReactNode }) => (
  <span className={styles.activityValue}>{children}</span>
);

const ATTENDANCE_STATUS_I18N: Record<
  string,
  { id: string; defaultMessage: string }
> = {
  show: { id: 'pages.serviceRequest.attended', defaultMessage: 'Attended' },
  no_show: { id: 'pages.serviceRequest.noShow', defaultMessage: 'No Show' },
};

/**
 * In-sentence before/after value for a serviceRequestUpdate payload — unwraps
 * `.value` and presents it per change type (assignee → person name,
 * branch service → name, attendance → translated status label).
 */
const renderUpdateValue = (
  type: LeadActivityType,
  update: any,
): React.ReactNode => {
  const value = update?.value;
  if (isEmpty(value)) return null;

  switch (type) {
    case LeadActivityEvents.appointment_assignee_changed:
      return <Value>{getEditorName(value) || toDisplayString(value)}</Value>;
    case LeadActivityEvents.appointment_attendance_status_changed: {
      const label = ATTENDANCE_STATUS_I18N[String(value)];
      return (
        <Value>
          {label ? <F {...label} /> : humanizeFieldKey(String(value))}
        </Value>
      );
    }
    default:
      return <Value>{toDisplayString(value)}</Value>;
  }
};

/** Renders a single before/after value as the in-sentence presentation for its change type. */
const renderChangeValue = (
  type: LeadActivityType,
  value: any,
): React.ReactNode => {
  if (isEmpty(value)) return null;

  switch (type) {
    case LeadActivityEvents.lead_state_changed:
      return <LeadStateTagPresentation state={value} />;
    case LeadActivityEvents.lead_priority_changed:
      return <LeadPriorityPresentation priority={value} />;
    case LeadActivityEvents.assignee_changed:
    case LeadActivityEvents.unassigned:
      return <Value>{getEditorName(value) || toDisplayString(value)}</Value>;
    default:
      return <Value>{toDisplayString(value)}</Value>;
  }
};

/**
 * "X changed from {from} to {to}." / "X set to {to}." — full sentences so every
 * language can order the placeholders naturally; drops gracefully when a side is missing.
 */
const ChangeSentence = ({
  base,
  defaults,
  from,
  to,
  values,
}: {
  base: string;
  defaults: { changed: string; set: string };
  from: React.ReactNode;
  to: React.ReactNode;
  values?: Record<string, React.ReactNode>;
}) => {
  if (from && to)
    return (
      <F
        id={`${base}.sentence`}
        defaultMessage={defaults.changed}
        values={{ from, to, ...values }}
      />
    );
  if (to)
    return (
      <F
        id={`${base}.sentenceSet`}
        defaultMessage={defaults.set}
        values={{ to, ...values }}
      />
    );
  return null;
};

// Static per-type sentences, used only when the appointment payload has no
// service/place/time to build the inline line from.
const APPOINTMENT_SENTENCE_FALLBACK: Partial<
  Record<LeadActivityType, { id: string; defaultMessage: string }>
> = {
  [LeadActivityEvents.appointment_requested]: {
    id: 'pages.lead.activities.appointmentRequested.sentence',
    defaultMessage: 'The customer requested a new appointment.',
  },
  [LeadActivityEvents.appointment_booked]: {
    id: 'pages.lead.activities.appointmentBooked.sentence',
    defaultMessage: 'A new appointment was booked.',
  },
  [LeadActivityEvents.appointment_cancelled]: {
    id: 'pages.lead.activities.appointmentCancelled.sentence',
    defaultMessage: 'The appointment was cancelled.',
  },
  [LeadActivityEvents.appointment_cancelled_by_customer]: {
    id: 'pages.lead.activities.appointmentCancelledByCustomer.sentence',
    defaultMessage: 'The customer cancelled the appointment.',
  },
  [LeadActivityEvents.appointment_declined]: {
    id: 'pages.lead.activities.appointmentDeclined.sentence',
    defaultMessage: 'The appointment request was declined.',
  },
  [LeadActivityEvents.call_back]: {
    id: 'pages.lead.activities.callback.sentence',
    defaultMessage: 'The customer requested a callback.',
  },
  [LeadActivityEvents.callback_from_location]: {
    id: 'pages.lead.activities.callbackFromLocation.sentence',
    defaultMessage: 'The customer requested a callback from a location.',
  },
};

/**
 * "Kitchen consultation at Munich Store on Wed, 3 Jul · 14:00" — the whole
 * appointment as one inline line, degrading gracefully when parts are missing.
 */
const buildAppointmentLine = (value: any): React.ReactNode | null => {
  // Prefer the service type name — the branch service name usually embeds the
  // branch name ("Beratung - Munich Store"), which would repeat the place part.
  const serviceText =
    value?.branch_service?.service_type?.name ||
    value?.branch_service?.name ||
    '';
  const placeText =
    toDisplayString(value?.branch_service?.branch) ||
    (typeof value?.where === 'string' ? value.where : '');
  const whenDay = isCallbackFromLocation(value)
    ? null
    : toValidDay(value?.when) ?? toValidDay(value?.utc_start_time);
  const whenText = whenDay
    ? `${formatDayPart(whenDay)} · ${formatTime(whenDay)}`
    : '';

  const service = serviceText ? <Value>{serviceText}</Value> : null;
  const place = placeText ? <Value>{placeText}</Value> : null;
  const when = whenText ? <Value>{whenText}</Value> : null;

  if (service && place && when)
    return (
      <F
        id="pages.lead.activities.appointmentLine"
        defaultMessage="{service} at {place} on {when}"
        values={{ service, place, when }}
      />
    );
  if (service && place)
    return (
      <F
        id="pages.lead.activities.appointmentLineNoWhen"
        defaultMessage="{service} at {place}"
        values={{ service, place }}
      />
    );
  if (place && when)
    return (
      <F
        id="pages.lead.activities.appointmentLineNoService"
        defaultMessage="At {place} on {when}"
        values={{ place, when }}
      />
    );
  if (service && when)
    return (
      <F
        id="pages.lead.activities.appointmentLineNoPlace"
        defaultMessage="{service} on {when}"
        values={{ service, when }}
      />
    );
  if (when)
    return (
      <F
        id="pages.lead.activities.appointmentLineOnlyWhen"
        defaultMessage="On {when}"
        values={{ when }}
      />
    );
  if (place)
    return (
      <F
        id="pages.lead.activities.appointmentLineOnlyPlace"
        defaultMessage="At {place}"
        values={{ place }}
      />
    );
  return service;
};

/**
 * One human-readable sentence per activity type — the only per-type logic left.
 * Layout is unified in ActivityCard; only the sentence (locale key + values) differs.
 */
const ActivitySentence = ({ activity }: { activity: LeadActivityModel }) => {
  const intl = useIntl();
  const type = activity.activity_type;
  const { prev_value: prev, new_value: next } = activity;

  const sentence = (() => {
    switch (type) {
      case LeadActivityEvents.created:
        return (
          <F
            id="pages.lead.activities.created.sentence"
            defaultMessage="The project was created."
          />
        );
      case LeadActivityEvents.archived:
        return (
          <F
            id="pages.lead.activities.archived.sentence"
            defaultMessage="The project was archived."
          />
        );
      case LeadActivityEvents.unarchived:
        return (
          <F
            id="pages.lead.activities.unarchived.sentence"
            defaultMessage="The project was restored from the archive."
          />
        );

      case LeadActivityEvents.lead_state_changed:
        return (
          <ChangeSentence
            base="pages.lead.activities.changedStatus"
            defaults={{
              changed: 'Status changed from {from} to {to}.',
              set: 'Status set to {to}.',
            }}
            from={renderChangeValue(type, prev)}
            to={renderChangeValue(type, next)}
          />
        );
      case LeadActivityEvents.lead_priority_changed:
        return (
          <ChangeSentence
            base="pages.lead.activities.priorityChanged"
            defaults={{
              changed: 'Priority changed from {from} to {to}.',
              set: 'Priority set to {to}.',
            }}
            from={renderChangeValue(type, prev)}
            to={renderChangeValue(type, next)}
          />
        );
      case LeadActivityEvents.branch_changed:
        return (
          <ChangeSentence
            base="pages.lead.activities.changedBranch"
            defaults={{
              changed: 'Branch changed from {from} to {to}.',
              set: 'Branch set to {to}.',
            }}
            from={renderChangeValue(type, prev)}
            to={renderChangeValue(type, next)}
          />
        );
      case LeadActivityEvents.product_group_changed:
        return (
          <ChangeSentence
            base="pages.lead.activities.productGroupChanged"
            defaults={{
              changed: 'Product group changed from {from} to {to}.',
              set: 'Product group set to {to}.',
            }}
            from={renderChangeValue(type, prev)}
            to={renderChangeValue(type, next)}
          />
        );
      case LeadActivityEvents.assignee_changed:
        return (
          <ChangeSentence
            base="pages.lead.activities.assigned"
            defaults={{
              changed: 'Reassigned from {from} to {to}.',
              set: 'Assigned to {to}.',
            }}
            from={renderChangeValue(type, prev)}
            to={renderChangeValue(type, next)}
          />
        );
      case LeadActivityEvents.unassigned: {
        const from = renderChangeValue(type, prev);
        return from ? (
          <F
            id="pages.lead.activities.unassigned.sentence"
            defaultMessage="{from} was unassigned from this project."
            values={{ from }}
          />
        ) : (
          <F
            id="pages.lead.activities.unassigned.sentenceFallback"
            defaultMessage="The assignee was removed."
          />
        );
      }
      case LeadActivityEvents.extra_field_changed: {
        // The card headline already names the field (getActivityTitle).
        // Blank sides must drop out of the sentence entirely — an empty
        // string still makes a truthy element, so test the text, not the node.
        const fromText = getExtraFieldValueText(prev);
        const toText = getExtraFieldValueText(next);
        const from = fromText ? <Value>{fromText}</Value> : null;
        const to = toText ? <Value>{toText}</Value> : null;
        if (from && !to)
          return (
            <F
              id="pages.lead.activities.fieldChanged.sentenceRemoved"
              defaultMessage="The value {from} was removed."
              values={{ from }}
            />
          );
        if (!from && !to)
          // Both sides blank — the timeline hides these (isEmptyExtraFieldChange);
          // kept as a defense for other consumers.
          return (
            <F
              id="pages.lead.activities.fieldChanged.sentenceFallback"
              defaultMessage="A custom field was updated."
            />
          );
        return (
          <ChangeSentence
            base="pages.lead.activities.fieldChanged"
            defaults={{
              changed: 'Changed from {from} to {to}.',
              set: 'Set to {to}.',
            }}
            from={from}
            to={to}
          />
        );
      }
      case LeadActivityEvents.customer_data_changed:
        // The changed fields render as detail rows; the sentence is only a
        // fallback for payloads with nothing displayable.
        if (getChangedCustomerFields(activity).length) return null;
        return (
          <F
            id="pages.lead.activities.customerChanged.sentence"
            defaultMessage="The customer's contact details were updated."
          />
        );

      // Appointment activities render as one inline line — "Service at Branch
      // on Day" — with the per-type static sentence only as a fallback when the
      // payload has none of those parts.
      case LeadActivityEvents.appointment_requested:
      case LeadActivityEvents.appointment_booked:
      case LeadActivityEvents.appointment_cancelled:
      case LeadActivityEvents.appointment_cancelled_by_customer:
      case LeadActivityEvents.appointment_declined:
      case LeadActivityEvents.call_back:
      case LeadActivityEvents.callback_from_location: {
        const line = buildAppointmentLine(activity.new_value);
        if (line) return line;
        const fallback = APPOINTMENT_SENTENCE_FALLBACK[type];
        return fallback ? <F {...fallback} /> : null;
      }

      case LeadActivityEvents.appointment_rescheduled:
        return (
          <F
            id="pages.lead.activities.rescheduledAppointment.sentence"
            defaultMessage="The appointment was moved to a new time."
          />
        );
      case LeadActivityEvents.appointment_rescheduled_by_customer:
        return (
          <F
            id="pages.lead.activities.appointmentRescheduledByCustomer.sentence"
            defaultMessage="The customer moved the appointment to a new time."
          />
        );
      case LeadActivityEvents.appointment_start_time_changed:
        return (
          <F
            id="pages.lead.activities.appointmentStartTimeChanged.sentence"
            defaultMessage="The appointment's start time was changed."
          />
        );
      case LeadActivityEvents.appointment_end_time_changed:
        return (
          <F
            id="pages.lead.activities.appointmentEndTimeChanged.sentence"
            defaultMessage="The appointment's end time was changed."
          />
        );
      case LeadActivityEvents.appointment_assignee_changed:
        return (
          <ChangeSentence
            base="pages.lead.activities.appointmentAssigneeChanged"
            defaults={{
              changed: 'Appointment assignee changed from {from} to {to}.',
              set: 'Appointment assigned to {to}.',
            }}
            from={renderUpdateValue(type, prev)}
            to={renderUpdateValue(type, next)}
          />
        );
      case LeadActivityEvents.appointment_branch_service_changed:
        return (
          <ChangeSentence
            base="pages.lead.activities.appointmentBranchServiceChanged"
            defaults={{
              changed: 'Service changed from {from} to {to}.',
              set: 'Service set to {to}.',
            }}
            from={renderUpdateValue(type, prev)}
            to={renderUpdateValue(type, next)}
          />
        );
      case LeadActivityEvents.appointment_attendance_status_changed: {
        const from = renderUpdateValue(type, prev);
        const to = renderUpdateValue(type, next);
        // The status can go back to null (e.g. an appointment edit resets it).
        if (from && !to)
          return (
            <F
              id="pages.lead.activities.appointmentAttendanceStatusChanged.sentenceCleared"
              defaultMessage="Attendance status was reset."
            />
          );
        return (
          <ChangeSentence
            base="pages.lead.activities.appointmentAttendanceStatusChanged"
            defaults={{
              changed: 'Attendance changed from {from} to {to}.',
              set: 'Attendance marked as {to}.',
            }}
            from={from}
            to={to}
          />
        );
      }
      case LeadActivityEvents.calendar_event_created:
        return (
          <F
            id="pages.lead.calendarEvent.created.sentence"
            defaultMessage="A new calendar event was created."
          />
        );
      case LeadActivityEvents.calendar_event_updated:
        return (
          <F
            id="pages.lead.calendarEvent.updated.sentence"
            defaultMessage="The calendar event was updated."
          />
        );
      case LeadActivityEvents.calendar_event_deleted:
        return (
          <F
            id="pages.lead.calendarEvent.deleted.sentence"
            defaultMessage="The calendar event was deleted."
          />
        );

      case LeadActivityEvents.call: {
        const from = next?.from ? (
          <PhoneNumberFormat phoneNumber={next.from} />
        ) : null;
        const duration = next?.duration
          ? getCallDuration(next.duration, intl)
          : null;
        if (from && duration)
          return (
            <F
              id="pages.lead.activities.call.sentence"
              defaultMessage="Call from {from}, duration {duration}."
              values={{ from, duration }}
            />
          );
        if (from)
          return (
            <F
              id="pages.lead.activities.call.sentenceNoDuration"
              defaultMessage="Call from {from}."
              values={{ from }}
            />
          );
        return (
          <F
            id="pages.lead.activities.call.sentenceFallback"
            defaultMessage="A call was logged."
          />
        );
      }

      case LeadActivityEvents.trickpark_session_added: {
        if (!next?.session_date) return null;
        const values = {
          project: next.session_project,
          date: dayjs(next.session_date).format('DD.MM.YYYY'),
          start: String(next.session_start || '').slice(0, 5),
          end: String(next.session_end || '').slice(0, 5),
        };
        return next.session_project ? (
          <F
            id="pages.lead.activities.trickparkSessionAdded.sentence"
            defaultMessage='Trickpark session "{project}" on {date}, {start}–{end}.'
            values={values}
          />
        ) : (
          <F
            id="pages.lead.activities.trickparkSessionAdded.sentenceNoProject"
            defaultMessage="Trickpark session on {date}, {start}–{end}."
            values={values}
          />
        );
      }

      default:
        return null;
    }
  })();

  if (!sentence) return null;
  return <div className={styles.activityCardSentence}>{sentence}</div>;
};

// Customer payload keys → existing attribute translations.
const CUSTOMER_FIELD_I18N: Record<string, string> = {
  firstname: 'common.attribute.first_name',
  lastname: 'common.attribute.last_name',
  email: 'common.attribute.email',
  phone: 'common.attribute.phone',
  address: 'common.attribute.address',
};

/** Extracts the changed customer fields as {key, from, to} display strings. */
const getChangedCustomerFields = (
  activity: LeadActivityModel,
): { key: string; from: string; to: string }[] => {
  const next = activity.new_value;
  const prev = activity.prev_value;
  if (!next || typeof next !== 'object' || Array.isArray(next)) return [];

  return Object.entries(next)
    .filter(([, value]) => value === null || typeof value !== 'object')
    .map(([key, value]) => ({
      key,
      from: prev && typeof prev === 'object' ? toDisplayString(prev[key]) : '',
      to: toDisplayString(value),
    }))
    .filter((field) => field.from || field.to);
};

/** One row per changed customer field: "Email: old@x.de → new@x.de" (old struck out). */
const CustomerDataDetails = ({ activity }: { activity: LeadActivityModel }) => {
  const intl = useIntl();
  const fields = getChangedCustomerFields(activity);
  if (!fields.length) return null;

  return (
    <div className={styles.activityDetails}>
      {fields.map(({ key, from, to }) => {
        const label = CUSTOMER_FIELD_I18N[key]
          ? intl.formatMessage({
              id: CUSTOMER_FIELD_I18N[key],
              defaultMessage: humanizeFieldKey(key),
            })
          : humanizeFieldKey(key);
        const hasChange = from && from !== to;

        return (
          <div key={key} className={styles.activityDetailRow}>
            <span className={styles.activityDetailLabel}>{`${label}:`}</span>
            {hasChange && (
              <span className={styles.activityOldValue}>{from}</span>
            )}
            {hasChange && to && (
              <ArrowRightOutlined className={styles.activityArrow} />
            )}
            {to && <Value>{to}</Value>}
          </div>
        );
      })}
    </div>
  );
};

/**
 * The uploaded file's name as a detail row. Deletions carry a null (or
 * pathless) new_value — nothing to show — so the card headline alone reads
 * "Deleted a file".
 */
const FileDetails = ({ activity }: { activity: LeadActivityModel }) => {
  const value = activity.new_value;
  if (isEmpty(value)) return null;

  const path = typeof value?.path === 'string' ? value.path : '';
  const fileName = path.split('/').pop();
  if (!fileName) return null;

  return (
    <div className={styles.activityDetails}>
      <div className={styles.activityDetailRow}>
        <FileOutlined />
        <Value>{fileName}</Value>
      </div>
    </div>
  );
};

/** "Wed, 3 Jul" this year, "Wed, 3 Jul 2027" otherwise — no redundant year. */
const formatDayPart = (date: dayjs.Dayjs): string =>
  date.year() === dayjs().year()
    ? date.format('ddd, D MMM')
    : date.format('ddd, D MMM YYYY');

const formatTime = (date: dayjs.Dayjs): string => date.format('HH:mm');

const formatTimeRange = (start: dayjs.Dayjs, end: dayjs.Dayjs | null): string =>
  end ? `${formatTime(start)} – ${formatTime(end)}` : formatTime(start);

const toValidDay = (value?: string): dayjs.Dayjs | null => {
  if (!value) return null;
  const date = dayjs(value);
  return date.isValid() ? date : null;
};

/**
 * serviceRequestUpdate raw values are UTC wall times without an offset
 * ("2026-07-15 14:00:00") — parse those as UTC and convert to local so they
 * line up with the offset-carrying ISO timestamps used everywhere else.
 */
const toValidUtcDay = (value?: unknown): dayjs.Dayjs | null => {
  if (!value || typeof value !== 'string') return null;
  const date = /Z|[+-]\d{2}:?\d{2}$/.test(value)
    ? dayjs(value)
    : dayjs.utc(value).local();
  return date.isValid() ? date : null;
};

/**
 * The reschedule time row: old value struck out next to the new one (as in the
 * reschedule emails). When both fall on the same day the date renders once and
 * only the times change: "Wed, 3 Jul · ~14:00 – 15:00~ → 16:00 – 17:00".
 */
const RescheduleTimeRow = ({ activity }: { activity: LeadActivityModel }) => {
  const isStartOnly =
    activity.activity_type ===
    LeadActivityEvents.appointment_start_time_changed;
  const isEndOnly =
    activity.activity_type === LeadActivityEvents.appointment_end_time_changed;
  const prev = activity.prev_value;
  const next = activity.new_value;

  let prefix = '';
  let oldText = '';
  let newText = '';

  if (isStartOnly || isEndOnly) {
    // serviceRequestUpdate shape ({property, value, service_request}) with a
    // fallback to the legacy serviceRequest morph payload. Only one boundary
    // changed; the other one is taken from the appointment for context:
    // end-only  → "day · start – ~old~ → new"
    // start-only → "day · ~old~ → new – end"
    const changedKey = isStartOnly ? 'utc_start_time' : 'utc_end_time';
    const fixedKey = isStartOnly ? 'utc_end_time' : 'utc_start_time';
    const prevTime =
      toValidUtcDay(prev?.value) ?? toValidDay(prev?.[changedKey]);
    const nextTime =
      toValidUtcDay(next?.value) ?? toValidDay(next?.[changedKey]);
    const fixedTime = toValidDay(
      next?.service_request?.[fixedKey] ?? next?.[fixedKey],
    );
    const sameDay = !prevTime || !nextTime || prevTime.isSame(nextTime, 'day');
    if (sameDay) {
      const day = nextTime ?? prevTime;
      if (day) {
        prefix = `${formatDayPart(day)} · ${
          isEndOnly && fixedTime ? `${formatTime(fixedTime)} –` : ''
        }`.trim();
      }
      oldText = prevTime ? formatTime(prevTime) : '';
      newText = nextTime ? formatTime(nextTime) : '';
      if (isStartOnly && newText && fixedTime) {
        newText = `${newText} – ${formatTime(fixedTime)}`;
      }
    } else {
      oldText = `${formatDayPart(prevTime)} · ${formatTime(prevTime)}`;
      newText = `${formatDayPart(nextTime)} · ${formatTime(nextTime)}`;
    }
  } else {
    const prevStart = toValidDay(prev?.utc_start_time);
    const prevEnd = toValidDay(prev?.utc_end_time);
    const nextStart = toValidDay(next?.utc_start_time);
    const nextEnd = toValidDay(next?.utc_end_time);
    if (prevStart && nextStart && prevStart.isSame(nextStart, 'day')) {
      prefix = `${formatDayPart(nextStart)} ·`;
      oldText = formatTimeRange(prevStart, prevEnd);
      newText = formatTimeRange(nextStart, nextEnd);
    } else {
      oldText = prevStart
        ? `${formatDayPart(prevStart)} · ${formatTime(prevStart)}`
        : '';
      newText = nextStart
        ? `${formatDayPart(nextStart)} · ${formatTimeRange(nextStart, nextEnd)}`
        : '';
    }
  }

  if (!oldText && !newText) return null;

  return (
    <div className={styles.activityDetailRow}>
      {prefix && <span>{prefix}</span>}
      {oldText && <span className={styles.activityOldValue}>{oldText}</span>}
      {oldText && newText && (
        <ArrowRightOutlined className={styles.activityArrow} />
      )}
      {newText && <Value>{newText}</Value>}
    </div>
  );
};

/**
 * Supplementary appointment rows: the reschedule time change (old struck out
 * next to the new one) and the cancellation reason. Everything else about the
 * appointment lives in the inline line rendered as the card's sentence.
 */
const AppointmentDetails = ({ activity }: { activity: LeadActivityModel }) => {
  const type = activity.activity_type;
  const value = activity.new_value;
  if (isEmpty(value)) return null;

  const reason =
    typeof value?.cancellation_reason === 'string'
      ? value.cancellation_reason
      : '';
  // Field-change activities name only the changed value in their sentence, so
  // a context row says which appointment it was ("At Munich Store on Wed, 3 Jul").
  const appointmentLine =
    SERVICE_REQUEST_UPDATE_TYPES.has(type) && !RESCHEDULE_TYPES.has(type)
      ? buildAppointmentLine(value?.service_request)
      : null;
  if (!RESCHEDULE_TYPES.has(type) && !reason && !appointmentLine) return null;

  return (
    <div className={styles.activityDetails}>
      {RESCHEDULE_TYPES.has(type) && <RescheduleTimeRow activity={activity} />}
      {appointmentLine && (
        <div className={styles.activityDetailRow}>{appointmentLine}</div>
      )}
      {reason && (
        <div className={styles.activityDetailRow}>
          <span className={styles.activityDetailLabel}>
            <F
              id="pages.serviceRequests.cancellationReason"
              defaultMessage="Cancellation Reason"
            />
            {':'}
          </span>
          <span>{reason}</span>
        </div>
      )}
    </div>
  );
};

const formatActivityFooterDate = (
  created: string,
  intl: {
    formatMessage: (descriptor: {
      id: string;
      defaultMessage: string;
    }) => string;
  },
): string | null => {
  const date = dayjs(created);
  if (!date.isValid()) return null;

  const today = dayjs().format('YYYY-MM-DD');
  const yesterday = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
  const formatted = date.format('YYYY-MM-DD');

  let dateLabel: string;
  if (formatted === today) {
    dateLabel = intl.formatMessage({
      id: 'common.today',
      defaultMessage: 'Today',
    });
  } else if (formatted === yesterday) {
    dateLabel = intl.formatMessage({
      id: 'pages.common.yesterday',
      defaultMessage: 'Yesterday',
    });
  } else {
    dateLabel = date.format('DD.MM');
  }

  return `${dateLabel}, ${date.format('HH:mm')}`;
};

// Plain text on purpose — the avatar+name tag is too attention-grabbing for a footer.
const ActivityAuthor = ({
  editor,
}: {
  editor: LeadActivityModel['editor'];
}) => {
  if (!isEmpty(editor)) return <span>{getEditorName(editor)}</span>;
  return (
    <span>
      <F id="pages.lead.activities.system" defaultMessage="System" />
    </span>
  );
};

/**
 * One activity as a compact card sitting next to its timeline icon: bold
 * headline, one human-readable sentence, optional appointment details, and a
 * light "date, time · author" footer. The whole card is clickable when the
 * activity references an entity that can be opened — no inner links.
 *
 * `hideTitle` drops the headline for single-type feeds (the comments drawer),
 * where repeating "Comment" on every card is noise.
 */
const ActivityCard = ({
  activity,
  hideTitle,
}: {
  activity: LeadActivityModel;
  hideTitle?: boolean;
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const type = activity.activity_type;

  const title = hideTitle ? null : getActivityTitle(activity, intl);

  const isComment = type === LeadActivityEvents.comment;
  const isTaskNote = type === LeadActivityEvents.task_note;
  // Comment and task-note both render as a free-text body (comment.comment /
  // taskNote.note) rather than a change sentence.
  const bodyText = isComment
    ? typeof activity.new_value === 'string'
      ? activity.new_value
      : activity.new_value?.comment
    : isTaskNote
      ? typeof activity.new_value === 'string'
        ? activity.new_value
        : activity.new_value?.note
      : null;
  // Comment payloads carry their author in new_value.author, not editor.
  const footerAuthor =
    isComment && isEmpty(activity.editor)
      ? activity.new_value?.author
      : activity.editor;

  const clickTarget = getActivityClickTarget(activity);
  const handleClick = clickTarget
    ? () =>
        dispatch(
          'appointmentId' in clickTarget
            ? {
                type: 'serviceRequest/toggleDetailsDrawer',
                payload: { appointmentId: clickTarget.appointmentId },
              }
            : {
                type: 'serviceRequest/toggleCalendarEventDetailsDrawer',
                payload: { calendarEventId: clickTarget.calendarEventId },
              },
        )
    : undefined;

  const footerDate = formatActivityFooterDate(activity.created, intl);

  return (
    <div
      onClick={handleClick}
      role={handleClick ? 'button' : undefined}
      className={classNames(styles.activityCard, {
        [styles.activityCardClickable]: !!handleClick,
      })}
    >
      {title && <div className={styles.activityCardTitle}>{title}</div>}
      {isComment || isTaskNote ? (
        bodyText && (
          <Typography.Paragraph
            className={styles.activityComment}
            ellipsis={{ rows: 3, expandable: true }}
          >
            {bodyText}
          </Typography.Paragraph>
        )
      ) : (
        <ActivitySentence activity={activity} />
      )}
      {APPOINTMENT_LINK_TYPES.has(type) && (
        <AppointmentDetails activity={activity} />
      )}
      {type === LeadActivityEvents.customer_data_changed && (
        <CustomerDataDetails activity={activity} />
      )}
      {FILE_TYPES.has(type) && <FileDetails activity={activity} />}
      <div className={styles.activityCardFooter}>
        {footerDate && (
          <>
            <span>{footerDate}</span>
            <span className={styles.activityCardDivider}>·</span>
          </>
        )}
        <ActivityAuthor editor={footerAuthor} />
      </div>
    </div>
  );
};

export default React.memo(ActivityCard);
